import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { deallocateShiftsFromActivityOccurrence } from "@/api/Activities";
import { unassignGroupFromShift } from "@/api/Shifts";
import { useErrorMessageAlert, deducedError } from "@/Utils/ErrorUtils";
import {
  activityOccurrenceStatusSchema,
  visitStatusSchema,
  type IActivityOccurrence,
} from "@medoma/models/activities";
import { type IVisit } from "@medoma/models/visits";
import { routeStatusSchema } from "@medoma/models/routes";
import type { IShiftWithContents } from "@medoma/models/shiftWithContents";

type UnassignActivitiesAndVisitsFromShiftResult = {
  totalOperations: number;
  failedOperations: number;
  failures: Array<{
    visit?: IVisit;
    activityOccurrence?: IActivityOccurrence;
    error: unknown;
  }>;
};

/**
 * Frontend only implementation of clearing a shift from activities and visits
 * A backend endpoint would be much better, but this is a quick fix for now
 */
export const useUnassignActivitiesAndVisitsFromShift = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (shiftWithContents: IShiftWithContents) => {
      const result: UnassignActivitiesAndVisitsFromShiftResult = {
        failures: [],
        totalOperations: 0,
        failedOperations: 0,
      };

      if (shiftWithContents.items.length === 0) {
        return result;
      }

      const shiftId = shiftWithContents.shift.id;
      const shiftItems = shiftWithContents.items;

      // Process each item in the shift's contents (Routes and Activity occurrences)
      for (const item of shiftItems) {
        // Check if item is a Route (has visits property)
        if ("visits" in item) {
          const route = item;
          // If route is finished, skip it
          if (route.status === routeStatusSchema.enum.finished) {
            continue;
          }
          for (const visit of route.visits) {
            // If visit is finished, skip it
            if (visit.status === visitStatusSchema.enum.finished) {
              continue;
            }
            const isShiftAssigned = visit.assignees.some(
              (assignee) => assignee.id === shiftId,
            );
            if (isShiftAssigned) {
              try {
                await unassignGroupFromShift(visit.id, shiftId);
              } catch (error) {
                result.failures.push({ visit, error });
                result.failedOperations++;
              }

              result.totalOperations++;
            }
          }
        }
        // Check if item is an Activity Occurrence (has activityId property)
        else if ("activityId" in item) {
          const activityOccurrence = item;
          // If activity occurrence is finished, skip it
          if (
            activityOccurrence.status ===
            activityOccurrenceStatusSchema.enum.finished
          ) {
            continue;
          }
          const isShiftAssigned = activityOccurrence.assignees.some(
            (assignee) => assignee.id === shiftId,
          );
          if (isShiftAssigned) {
            try {
              await deallocateShiftsFromActivityOccurrence(
                [shiftId],
                activityOccurrence.activityId,
                activityOccurrence.id,
              );
            } catch (error) {
              result.failures.push({ activityOccurrence, error });
              result.failedOperations++;
            }
            result.totalOperations++;
          }
        }
      }

      // Any failures are optionally handled by the caller
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort aktiviteter och besök från arbetspasset.`} ${deducedError(error)}`,
      );
    },
  });

  return {
    mutateAsync,
    isPending,
  };
};
